import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import {
  lightGreenBG,
  greenTextColor,
  secondaryText,
  whiteBG,
  borderPrimary,
  semiBoldText,
  smallTextSize,
} from '../../styling/stylingHelper';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface FilterOption {
  value: string;
  label: string;
}

interface DropdownSelectorProps {
  options: FilterOption[];
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
  alwaysFullWidth?: boolean;
}

export const DropdownSelector = ({
  options,
  defaultValue,
  onChange,
  className,
  alwaysFullWidth = false,
}: DropdownSelectorProps) => {
  const [selectedValue, setSelectedValue] = useState(defaultValue || options[0]?.value || '');
  const [isOpen, setIsOpen] = useState(false);

  // Update internal state when defaultValue prop changes
  React.useEffect(() => {
    if (defaultValue) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    onChange?.(value);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === selectedValue);
  const displayText = selectedOption ? selectedOption.label : '';

  // Outer dropdown container: ONLY border on left (NO bottom border), and rounded corners
  const optionDropdownContainer =
    'absolute top-full left-0 mt-2 w-full z-10 bg-white shadow-lg rounded-xl border-l ' +
    'border-borderPrimary dark:border-borderPrimary-dark';

  return (
    <div
      className={cn(
        className,
        "relative block",
        smallTextSize,
        semiBoldText,
        "w-full"
      )}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          'inline-flex items-center justify-between px-3 py-2 border rounded-xl',
          alwaysFullWidth ? 'w-full' : 'max-[950px]:w-full',
          whiteBG,
          secondaryText,
          borderPrimary
        )}
      >
        <span className="truncate">{displayText}</span>
        <span className="ml-2">
          {isOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </span>
      </button>
      {isOpen && (
        <div className={cn(optionDropdownContainer, smallTextSize, semiBoldText)}>
          {options.map((opt, idx) => {
            const isSelected = selectedValue === opt.value;
            const bgClass = isSelected ? lightGreenBG : whiteBG;
            const textClass = isSelected ? greenTextColor : secondaryText;
            // Rounding for first and last options inside the dropdown
            const isFirst = idx === 0;
            const isLast = idx === options.length - 1;
            const roundedClass = cn(
              isFirst && 'rounded-t-xl',
              isLast && 'rounded-b-xl'
            );
            // Only add bottom border if not the last item, NO left/right border!
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={cn(
                  'w-full text-left px-3 py-2 block hover:bg-gray-50 dark:hover:bg-white/5 focus:outline-none rounded-xl',
                  bgClass,
                  textClass,
                  roundedClass,
                )}
                style={{
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};